import 'regenerator-runtime/runtime';

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { loadImageFromSrc, getDataURLFromUint8Array } from '../utils/image';
import addFancyTouchListener from '../utils/fancytouch';
import { ResourcePropType } from '../constants/propTypes';

import wasmFile from '../../emscripten/binary/gleditor.out.wasm';
import dataFile from '../../emscripten/binary/gleditor.out.data';
import GLEditorModule from '../../emscripten/gleditor.out';

const DEFAULT_CANVAS_RECT = { width: 500, height: 300, left: 0, top: 0 };

const wrapExportedFunctions = (module) => ({
  defineFixCSSDimensions: module.cwrap('defineFixCSSDimensions', null, [
    'number',
  ]),
  defineOnTextButtonPressed: module.cwrap('defineOnTextButtonPressed', null, [
    'number',
  ]),
  defineOnSelectionChanged: module.cwrap('defineOnSelectionChanged', null, [
    'number',
  ]),
  defineOnHistoryChanged: module.cwrap('defineOnHistoryChanged', null, [
    'number',
  ]),
  defineOnStickersRequested: module.cwrap('defineOnStickersRequested', null, [
    'number',
  ]),
  requestQuit: module.cwrap('requestQuit', null),
  setStickerURLForKey: module.cwrap('setStickerURLForKey', null, [
    'string',
    'string',
  ]),
  setStickerImageData: module.cwrap('setStickerImageData', null, [
    'string',
    'number',
    'number',
    'number',
  ]),
  setCanvasSize: module.cwrap('setCanvasSize', null, [
    'number',
    'number',
    'number',
  ]),
  setColorBG: module.cwrap('setColorBG', null, ['number', 'number', 'number']),
  startSingle: module.cwrap('startSingle', null, ['number', 'number']),
  moveSingle: module.cwrap('moveSingle', null, ['number', 'number']),
  endSingle: module.cwrap('endSingle', null),
  startDouble: module.cwrap('startDouble', null, [
    'number',
    'number',
    'number',
    'number',
  ]),
  moveDouble: module.cwrap('moveDouble', null, [
    'number',
    'number',
    'number',
    'number',
  ]),
  endDouble: module.cwrap('endDouble', null),
  rotateImage: module.cwrap('rotateImage', null),
  clipboardLineCount: module.cwrap('clipboardLineCount', 'number'),
  clipboardGetLine: module.cwrap('clipboardGetLine', 'string', ['number']),
  clipboardPush: module.cwrap('clipboardPush', null, ['string']),
  clipboardClear: module.cwrap('clipboardClear', null),
  addElement: module.cwrap('addElement', null, ['string']),
  addElementWithClipboardText: module.cwrap(
    'addElementWithClipboardText',
    null,
    ['string'],
  ),
  addSticker: module.cwrap('addSticker', null, ['string', 'string']),
  setClipboardToSelectedText: module.cwrap('setClipboardToSelectedText', null, [
    'string',
  ]),
  setSelectedTextFromClipboard: module.cwrap(
    'setSelectedTextFromClipboard',
    null,
  ),
  setActiveColor: module.cwrap('setActiveColor', null, [
    'number',
    'number',
    'number',
    'boolean',
  ]),
  setActiveThickness: module.cwrap('setActiveThickness', null, [
    'number',
    'boolean',
  ]),
  setActiveHighlighterMode: module.cwrap('setActiveHighlighterMode', null, [
    'boolean',
    'boolean',
  ]),
  setActiveFillMode: module.cwrap('setActiveFillMode', null, [
    'boolean',
    'boolean',
    'boolean',
  ]),
  setSketchModeEnabled: module.cwrap('setSketchModeEnabled', null, ['boolean']),
  polylineIsSelected: module.cwrap('polylineIsSelected', 'boolean'),
  deselect: module.cwrap('deselect', null),
  getColorAdjustBrightness: module.cwrap('getColorAdjustBrightness', 'number'),
  getColorAdjustContrast: module.cwrap('getColorAdjustContrast', 'number'),
  getColorAdjustSaturation: module.cwrap('getColorAdjustSaturation', 'number'),
  setColorAdjustBrightness: module.cwrap('setColorAdjustBrightness', null, [
    'number',
    'boolean',
  ]),
  setColorAdjustContrast: module.cwrap('setColorAdjustContrast', null, [
    'number',
    'boolean',
  ]),
  setColorAdjustSaturation: module.cwrap('setColorAdjustSaturation', null, [
    'number',
    'boolean',
  ]),
  cropEnableAndEdit: module.cwrap('cropEnableAndEdit', null),
  cropDisableAndReset: module.cwrap('cropDisableAndReset', null),
  cropStopEditing: module.cwrap('cropStopEditing', null),
  undo: module.cwrap('undo', null),
  redo: module.cwrap('redo', null),
  clearAll: module.cwrap('clearAll', null),
  loadJSON: module.cwrap('loadJSON', null, ['string']),
  saveJSONToClipboard: module.cwrap('saveJSONToClipboard', null),
  setImageData: module.cwrap('setImageData', null, [
    'number',
    'number',
    'number',
  ]),
  getOutputImageWidth: module.cwrap('getOutputImageWidth', 'number'),
  getOutputImageHeight: module.cwrap('getOutputImageHeight', 'number'),
  getOutputImage: module.cwrap('getOutputImage', null, ['number']),
});

class ModuleWrapper extends Component {
  constructor(props) {
    super(props);

    this.module = null; // Raw module object
    this.em = {}; // Exported module functions wrapped via cwrap

    this.pixelRatio = window.devicePixelRatio;

    this.state = { ...DEFAULT_CANVAS_RECT };

    this.refCanvas = React.createRef();
  }

  componentDidMount() {
    this.props.setInitializer(this.initialize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.stickerKeyURLs !== this.props.stickerKeyURLs) {
      this.updateStickerURLs();
    }
  }

  componentWillUnmount() {
    if (typeof this.removeFancyTouchListener === 'function') {
      this.removeFancyTouchListener();
    }
    try {
      this.em.requestQuit();
    } catch (e) {
      // console.log('Quit threw exception (expected)', e);
    }
  }

  updateStickerURLs = () => {
    if (!this.em.setStickerURLForKey) {
      return;
    }
    this.props.stickerKeyURLs.forEach(({ key, src: url }) => {
      this.em.setStickerURLForKey(key, url);
    });
  };

  onStickersRequested = () => {
    const requestedURLs = [];
    const lineCount = this.em.clipboardLineCount();
    for (let i = 0; i < lineCount; i++) {
      requestedURLs.push(this.em.clipboardGetLine(i));
    }

    requestedURLs.forEach(async (url) => {
      console.log('REQUESTED URL: ', url);
      try {
        const { imgData } = await loadImageFromSrc({
          src: url,
          returnImageData: true,
        });
        const bytes = imgData.width * imgData.height * 4;
        const ptr = this.module._malloc(bytes);
        this.module.HEAPU8.set(imgData.data, ptr);
        this.em.setStickerImageData(url, ptr, imgData.width, imgData.height);
        this.module._free(ptr);
      } catch (e) {
        console.log(e);
      }
    });
  };

  initialize = ({
    initialCanvasRect = DEFAULT_CANVAS_RECT,
    logger = () => {},
  } = {}) =>
    new Promise((resolve) => {
      this.setState(initialCanvasRect, () => {
        // NOTE: The Module is not a true Promise, and will freeze if 'await'ed
        GLEditorModule({
          canvas: this.refCanvas.current,
          print: logger,
          printErr: logger,
          setStatus: logger,
          monitorRunDependencies: (n) => logger(`Loading: ${n} left`),
          locateFile: (path) =>
            path.slice(-5) === '.wasm'
              ? wasmFile
              : path.slice(-5) === '.data'
              ? dataFile
              : '',
          arguments: [
            `${initialCanvasRect.width}`,
            `${initialCanvasRect.height}`,
            `${this.pixelRatio}`,
          ],
        }).then((module) => {
          this.module = module;
          this.em = wrapExportedFunctions(module);

          this.setModuleCallbacks();
          this.updateStickerURLs();
          this.fixCSSDimensions();

          this.removeFancyTouchListener = addFancyTouchListener(
            this.refCanvas.current,
            this.makeTouchHandlers(),
          );
          resolve(this.makeModuleMethods());
        });
      });
    });

  setModuleCallbacks = () => {
    // NOTE: Must set RESERVED_FUNCTION_POINTERS to the number of functions
    // here (i.e., 5) in companycam-gleditor emscripten/CMakeLists.txt
    this.em.defineFixCSSDimensions(
      this.module.addFunction(this.fixCSSDimensions, 'v'),
    );
    this.em.defineOnTextButtonPressed(
      this.module.addFunction(this.props.onTextButtonPressed, 'v'),
    );
    this.em.defineOnSelectionChanged(
      this.module.addFunction(this.props.onSelectionChanged, 'v'),
    );
    this.em.defineOnHistoryChanged(
      this.module.addFunction(this.props.onHistoryChanged, 'vii'),
    );
    this.em.defineOnStickersRequested(
      this.module.addFunction(this.onStickersRequested, 'v'),
    );
  };

  makeTouchHandlers = () => ({
    startSingle: (p) => this.em.startSingle(p[0], p[1]),
    moveSingle: (p) => this.em.moveSingle(p[0], p[1]),
    endSingle: () => this.em.endSingle(),
    startDouble: (p, q) => this.em.startDouble(p[0], p[1], q[0], q[1]),
    moveDouble: (p, q) => this.em.moveDouble(p[0], p[1], q[0], q[1]),
    endDouble: () => this.em.endDouble(),
  });

  makeModuleMethods = () => ({
    setCanvasRect: (rect) => {
      this.setState(rect);
      this.em.setCanvasSize(rect.width, rect.height, this.pixelRatio);
    },

    setColorBG: ([r, g, b]) => this.em.setColorBG(r / 255, g / 255, b / 255),

    rotateImage: this.em.rotateImage,

    addElement: (type, lines = null) => {
      if (lines) {
        this.em.clipboardClear();
        lines.forEach((line) => this.em.clipboardPush(line));
        this.em.addElementWithClipboardText(type);
        this.em.clipboardClear();
      } else {
        this.em.addElement(type);
      }
    },

    addSticker: this.em.addSticker,

    getSelectedText: () => {
      this.em.setClipboardToSelectedText();
      const lines = [];
      const lineCount = this.em.clipboardLineCount();
      for (let i = 0; i < lineCount; i++) {
        lines.push(this.em.clipboardGetLine(i));
      }
      return lines;
    },

    setSelectedText: (lines) => {
      this.em.clipboardClear();
      lines.forEach((line) => this.em.clipboardPush(line));
      this.em.setSelectedTextFromClipboard();
      this.em.clipboardClear();
    },

    setActiveColor: ([r, g, b], applyToSelected) =>
      this.em.setActiveColor(r / 255, g / 255, b / 255, applyToSelected),
    setActiveThickness: this.em.setActiveThickness,
    setActiveHighlighterMode: this.em.setActiveHighlighterMode,
    setActiveFillMode: ({ closePath, fillPath }, applyToSelected) =>
      this.em.setActiveFillMode(closePath, fillPath, applyToSelected),

    setSketchModeEnabled: this.em.setSketchModeEnabled,
    polylineIsSelected: this.em.polylineIsSelected,
    deselect: this.em.deselect,

    getColorAdjust: () => ({
      brightness: this.em.getColorAdjustBrightness(),
      contrast: this.em.getColorAdjustContrast(),
      saturation: this.em.getColorAdjustSaturation(),
    }),

    setColorAdjust: ({ brightness, contrast, saturation, pushHistory }) => {
      this.em.setColorAdjustBrightness(brightness, false);
      this.em.setColorAdjustContrast(contrast, false);
      this.em.setColorAdjustSaturation(saturation, !!pushHistory);
    },

    cropEnableAndEdit: this.em.cropEnableAndEdit,
    cropDisableAndReset: this.em.cropDisableAndReset,
    cropStopEditing: this.em.cropStopEditing,
    undo: this.em.undo,
    redo: this.em.redo,
    clearAll: this.em.clearAll,
    loadJSON: this.em.loadJSON,

    saveJSON: () => {
      this.em.saveJSONToClipboard();
      return this.em.clipboardGetLine(0);
    },

    setImageData: async (imgData) => {
      const bytes = imgData.width * imgData.height * 4;
      const ptr = this.module._malloc(bytes);
      this.module.HEAPU8.set(imgData.data, ptr);
      this.em.setImageData(ptr, imgData.width, imgData.height);
      this.module._free(ptr);
    },

    getOutputImageDataURL: async ({ jpgQuality = 0.8 } = {}) => {
      const w = this.em.getOutputImageWidth();
      const h = this.em.getOutputImageHeight();

      const bytes = w * h * 4;
      const ptr = this.module._malloc(bytes);
      this.em.getOutputImage(ptr);
      const pixelData = this.module.HEAPU8.subarray(ptr, ptr + bytes);
      const dataURL = await getDataURLFromUint8Array({
        data: pixelData,
        width: w,
        height: h,
        jpgQuality,
      });
      this.module._free(ptr);
      return { width: w, height: h, dataURL };
    },
  });

  fixCSSDimensions = () => {
    if (this.refCanvas.current) {
      this.refCanvas.current.style.width = `${this.state.width}px`;
      this.refCanvas.current.style.height = `${this.state.height}px`;
    }
  };

  render() {
    // NOTE: canvas.width/height get set by Emscripten in setCanvasSize.
    // But, canvas.style.width/height get *stripped* by Emscripten.
    // Therefore, we have the native code request they get added back in
    // via 'fixCSSDimensions' any time it sets the canvas size.
    // cf: https://github.com/emscripten-core/emscripten/issues/6353
    return (
      <canvas
        ref={this.refCanvas}
        style={{
          position: 'absolute',
          left: `${this.state.left}px`,
          top: `${this.state.top}px`,
        }}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
      />
    );
  }
}

ModuleWrapper.propTypes = {
  stickerKeyURLs: PropTypes.arrayOf(ResourcePropType),
  onTextButtonPressed: PropTypes.func,
  onSelectionChanged: PropTypes.func,
  onHistoryChanged: PropTypes.func,
  setInitializer: PropTypes.func,
};

ModuleWrapper.defaultProps = {
  stickerKeyURLs: [],
  onTextButtonPressed: () => {}, // () => void
  onSelectionChanged: () => {}, // () => void
  onHistoryChanged: () => {}, // (canUndo, canRedo) => void
  setInitializer: () => {}, // (initializer) => void
};

export default ModuleWrapper;
